import { useCallback, useEffect, useMemo, useState } from "react";
import type { ChangeEvent } from "react";
import { Box, Input as MuiInput, Typography } from "@mui/material";
import { yupResolver } from "@hookform/resolvers/yup";
import { Link, useNavigate } from "react-router-dom";
import { useForm } from "react-hook-form";
import PowerSettingsNewIcon from "@mui/icons-material/PowerSettingsNew";

import { ReactComponent as SearchIcon } from "../../assets/svg/Search.svg";
import { LabelBox, Button } from "../../components";
import { colors } from "../../theme/colors";
import { useGetClinicalDiagramsQuery, useUpdateClinicalDiagramDetailsMutation } from "../../services/api";
import { CustomTable, StatusBadge } from "../../components/DataTable";
import style from "./clinicalDiagram.module.css";
import { CustomActionMenu } from "../../components/Menu";
import type { ActionItem } from "../../components/Menu";
import { ReactComponent as DeactivateIcon } from "../../assets/svg/deactivate.svg";
import { ReactComponent as EditIcon } from "../../assets/svg/edit.svg";
import { ReactComponent as EyeIcon } from "../../assets/svg/eye.svg";
import Logs from "../../assets/images/logs.png";
import { ConfirmationDialog, DetailsDialog, InputDialog } from "../../components/Dialog";
import { updateStatusSchema, } from "../../yup";
import Input from "../../components/UserInput/Input";
import type { ApiError, ClinicalDiagramQueryParams } from "./clinicalDiagramTypes";
import { getApiErrorMessage } from "./clinicalDiagramUtils";

type ClinicalDiagramAction =
  | "view"
  | "edit"
  | "logs"
  | "deactivate"
  | "activate";
type UpdateStatusFormValues = {
  deactivate_reason: string;
};

/**
 * Renders the Clinical Diagrams list view with search, actions, and status updates.
 *
 * The component fetches clinical diagrams, supports debounced search, and
 * provides per-row actions (view, edit, logs, activate/deactivate).
 *
 * Returns:
 *   JSX.Element: The Clinical Diagrams page content.
 */
const ClinicalDiagrams = () => {
  const [updateClinicalDiagramDetails] = useUpdateClinicalDiagramDetailsMutation();
  const navigate = useNavigate();

  // all query parameters and pagination params
  const [queryParam, setQueryParam] = useState<ClinicalDiagramQueryParams>({
    order_by_columns: "name",
    order: "Asc",
    is_detailed: true,
    search_key: "",
  });
  const [searchValue, setSearchValue] = useState(queryParam.search_key);
  const { data: clinicalDiagramData, isFetching } = useGetClinicalDiagramsQuery(queryParam);

  const [message, setMessage] = useState<LabelMessageType | null>(null);
  const [modalVisible, setModalVisible] = useState(false);
  const [selectedClinicalDiagram, setSelectedClinicalDiagram] = useState<ClinicalDiagram | null>(
    null
  );
  const [reasonModalVisible, setReasonModalVisible] = useState(false);
  const [logModalVisible, setLogModalVisible] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  // for handling the clinical diagram state
  const actionHandlers = useMemo<Record<ClinicalDiagramAction, (item: ClinicalDiagram) => void>>(
    () => ({
      deactivate: (item: ClinicalDiagram) => {
        setSelectedClinicalDiagram(item);
        setModalVisible(true);
      },
      activate: (item: ClinicalDiagram) => {
        setSelectedClinicalDiagram(item);
        setModalVisible(true);
      },
      view: (item: ClinicalDiagram) => {
        navigate(`/clinical-diagrams/details/${item?.id}`);
      },
      edit: (item: ClinicalDiagram) => {
        navigate(`/clinical-diagrams/edit/${item?.id}`);
      },
      logs: (item: ClinicalDiagram) => {
        setSelectedClinicalDiagram(item);
        setLogModalVisible(true);
      },
    }),
    [navigate]
  );

  const handleMenu = useCallback(
    (value: ClinicalDiagramAction, item: ClinicalDiagram) => {
      actionHandlers[value](item);
    },
    [actionHandlers]
  );

  // update the fields
  const fields = useMemo(
    () => [
      { id: "name", label: "Clinical Diagram Name", flex: 2, sortable: true },
      {
        id: "is_active",
        label: "Status",
        type: "badge",
        flex: 2,
        sortable: true,
        render: (item: ClinicalDiagram) => <StatusBadge isActive={item.is_active ?? false} />,
      },
      {
        id: "logo_s3_key_name",
        label: "Actions",
        type: "action",
        render: (item: ClinicalDiagram) => (
          <CustomActionMenu<ClinicalDiagram, ClinicalDiagramAction>
            item={item}
            isActive={item.is_active ?? false}
            handleChange={handleMenu}
            actions={
              [
                {
                  label: "View",
                  value: "view",
                  icon: <EyeIcon height={24} width={24} />,
                },
                ...(item.is_active
                  ? ([
                    {
                      label: "Edit",
                      value: "edit",
                      icon: <EditIcon height={24} width={24} />,
                    },
                  ] as ActionItem<ClinicalDiagramAction>[])
                  : []),
                {
                  label: "Logs",
                  value: "logs",
                  icon: <img src={Logs} alt="Logs" width={24} />,
                },
                {
                  label: item.is_active ? "Deactivate" : "Activate",
                  value: item.is_active ? "deactivate" : "activate",
                  icon: item.is_active ? (
                    <DeactivateIcon height={24} width={24} />
                  ) : (
                    <PowerSettingsNewIcon height={24} width={24} />
                  ),
                },
              ] as ActionItem<ClinicalDiagramAction>[]
            }
          />
        ),
      },
    ],
    [handleMenu]
  );

  // for searching in the query
  const handleSearch = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      setSearchValue(e.target.value);
    },
    []
  );

  useEffect(() => {
    const timeoutId = window.setTimeout(() => {
      setQueryParam((prevParams) => ({
        ...prevParams,
        search_key: searchValue,
      }));
    }, 300);

    return () => {
      window.clearTimeout(timeoutId);
    };
  }, [searchValue]);

  const {
    register,
    handleSubmit,
    formState: { errors },
    setValue,
    clearErrors,
  } = useForm<UpdateStatusFormValues>({
    resolver: yupResolver(updateStatusSchema),
  });

  const onSubmit = async (values: UpdateStatusFormValues) => {
    if (!selectedClinicalDiagram) {
      return;
    }
    // Guard against invalid selection to prevent toggling an unintended record.
    if (!selectedClinicalDiagram.id) {
      setMessage({
        type: "error",
        text: "Missing clinical diagram identifier. Please refresh and try again.",
      });
      return;
    }
    try {
      setIsLoading(true);
      const { deactivate_reason } = values;
      const isActive = Boolean(selectedClinicalDiagram.is_active);
      // API expects a single status toggle payload; reason is required only for deactivation.
      await updateClinicalDiagramDetails({
        is_active: !isActive,
        id: selectedClinicalDiagram.id,
        deactivate_reason,
      }).unwrap();
      setIsLoading(false);
      setReasonModalVisible(false);
      setValue("deactivate_reason", "");
      clearErrors();
    } catch (error) {
      setIsLoading(false);
      setMessage({
        type: "error",
        text: getApiErrorMessage(error as ApiError, "Something went wrong!"),
      });
    }
  };

  // Render the Clinical Diagram component
  return (
    <Box>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          marginTop: "15px",
        }}
      >
        <MuiInput
          disableUnderline
          placeholder="Search by name...."
          type="search"
          startAdornment={<SearchIcon width={20} height={20} />}
          value={searchValue}
          onChange={handleSearch}
          sx={{ width: "550px", gap: "8px", borderRadius: "50px", backgroundColor: colors.white }}
        />
        <Link to="add">
          <Button
            variant="contained"
            className={style.addButton}
            label="Add New Clinical Diagram"
            sx={{
              ":hover": { backgroundColor: colors.primary },
            }}
          />
        </Link>
      </Box>
      {message && (
        <Box mt={2}>
          <LabelBox
            boxProps={{ height: 50, display: "flex", alignItems: "center" }}
            data={message}
            onClose={() => setMessage(null)}
          />
        </Box>
      )}
      <Box style={{ maxHeight: "calc(100vh - 195px)", overflow: "auto" }}>
        <CustomTable<ClinicalDiagram>
          fields={fields}
          paginationRequired={true}
          data={clinicalDiagramData?.items || []}
          loading={isFetching}
          onDoubleClick={(clinicalDiagram: ClinicalDiagram) => {
            navigate(`/clinical-diagrams/details/${clinicalDiagram?.id}`);
          }}
          defaultOrderBy="name"
          searchTerm={searchValue}
          onClearSearch={() => setSearchValue("")}
        />
      </Box>
      {modalVisible && (
        <ConfirmationDialog
          visible={modalVisible}
          message={`Are you sure you want to ${selectedClinicalDiagram?.is_active ? "deactivate " : "activate "
            }"${selectedClinicalDiagram?.name}"?`}
          onClose={() => {
            setModalVisible(false);
          }}
          onConfirm={() => {
            setModalVisible(false);
            // Deactivation requires a reason; activation does not.
            if (selectedClinicalDiagram?.is_active) {
              setReasonModalVisible(true);
            } else {
              onSubmit({ deactivate_reason: "" });
            }
          }}
        />
      )}
      {reasonModalVisible && (
        <InputDialog
          visible={reasonModalVisible}
          onClose={() => {
            setReasonModalVisible(false);
            setValue("deactivate_reason", "");
            clearErrors();
          }}
        >
          <Box
            component="form"
            sx={{ marginX: "20px" }}
            onSubmit={handleSubmit(onSubmit)}
          >
            <Typography
              variant="h2"
              component="div"
              fontSize={16}
              fontWeight={700}
              color={colors.modalHeading}
              marginBottom={"10px"}
              sx={{ display: "flex", flexDirection: "row" }}
            >
              {"Enter the reason for deactivation"}
              <Typography
                color={"red"}
                fontSize={16}
                fontWeight={700}
                sx={{ marginLeft: "5px", marginTop: "-5px" }}
              >
                {"*"}
              </Typography>
            </Typography>
            <Input
              placeholder=""
              name="deactivate_reason"
              required
              register={register}
              multiline
              rows={3}
              errors={errors}
              sx={{
                width: "100%",
                margin: "1px",
                borderWidth: "1px",
              }}
            />
            <Box className={style.horizontalButtons} sx={{ paddingX: "50px" }}>
              <Button
                sx={{
                  width: "150px",
                  height: "35px",
                  backgroundColor: colors.white,
                  ":hover": { backgroundColor: colors.white },
                }}
                variant="outlined"
                onClick={() => {
                  setReasonModalVisible(false);
                  setValue("deactivate_reason", "");
                  clearErrors();
                }}
                label={"Cancel"}
              />
              <Button
                sx={{
                  width: "150px",
                  height: "35px",
                  ":hover": { backgroundColor: colors.primary },
                }}
                variant="contained"
                type="submit"
                label={"Submit"}
                loading={isLoading}
              />
            </Box>
          </Box>
        </InputDialog>
      )}
      {logModalVisible && (
        <DetailsDialog
          visible={logModalVisible}
          details={selectedClinicalDiagram}
          onClose={() => {
            setLogModalVisible(false);
          }}
          onConfirm={() => {
            setLogModalVisible(false);
          }}
        />
      )}
    </Box>
  );
}
export default ClinicalDiagrams;
